import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import ErrorDialog from '../components/ui/ErrorDialog';
import Modal from '../components/ui/Modal';
import { toast } from 'react-hot-toast';
import { Eye, EyeOff, User, Mail, Lock, Edit3, Search, X, Loader2, ChevronRight, ChevronDown } from 'lucide-react';
import { api } from '../services/authService';
import { FASTCUBE_ORG, organizationsService } from '../services/organizationsService';
import OrganizationPropertiesEditor from '../components/OrganizationPropertiesEditor';
import { getCookie, setCookieWithMinutes, deleteCookie } from '../services/cookieService';
import { getAdminUsersListCacheMinutes } from '../services/cookieConfigService';
import { useUploadPolling } from '../contexts/UploadPollingContext';
import { softWrap } from '../utils/softWrap';

const ADMIN_USERS_LIST_COOKIE = 'admin_users_list';

const BULK_METADATA_FIELDS = [
  'supplier_name',
  'purchase_date',
  'quality',
  'domains',
  'cost',
  'agreement',
];
const BULK_METADATA_FIELD_LABELS = {
  supplier_name: 'Supplier name',
  purchase_date: 'Purchase date',
  quality: 'Quality',
  domains: 'Domains',
  cost: 'Cost',
  agreement: 'Agreement',
};
const BULK_COLUMN_DETAIL_KEYS = [
  'email_field',
  'email_md5_field',
  'first_name_field',
  'last_name_field',
  'gender_field',
  'dob_field',
  'phone_number_field',
  'optin_ip_address_field',
  'optin_site_field',
  'address_field',
  'city_field',
  'state_field',
  'zipcode_field',
  'optin_date_field',
];
const BULK_EXPANDED_FIELD_LABELS = {
  email_field: 'Email column',
  email_md5_field: 'Email MD5 column',
  first_name_field: 'First name column',
  last_name_field: 'Last name column',
  gender_field: 'Gender column',
  dob_field: 'DOB column',
  phone_number_field: 'Phone number column',
  optin_ip_address_field: 'Opt-in IP address column',
  optin_site_field: 'Opt-in site column',
  address_field: 'Address column',
  city_field: 'City column',
  state_field: 'State column',
  zipcode_field: 'Zipcode column',
  optin_date_field: 'Opt-in date column',
};

const formatBulkDetailValue = (val) => {
  if (val == null || val === '') return '—';
  if (typeof val === 'number') return String(val);
  const s = String(val).trim();
  return s || '—';
};

function formatFileNameForDisplay(name) {
  return softWrap(name, 30);
}

const approvalStatusBadgeClass = (status) => {
  if (status === 'approved') return 'bg-green-100 text-green-800';
  if (status === 'rejected') return 'bg-red-100 text-red-800';
  if (status === 'waiting') return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-800';
};

const importStatusBadgeClass = (status) => {
  if (status === 'complete') return 'bg-green-100 text-green-800';
  if (status === 'failed' || status === 'error') return 'bg-red-100 text-red-800';
  if (status === 'started' || status === 'pending') return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-800';
};

const isUsersListCacheValid = (users) =>
  Array.isArray(users) && users.every((u) => Array.isArray(u.organizations));

const compareUsersByFirstName = (a, b) =>
  (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' })
  || (a.last_name || '').localeCompare(b.last_name || '', undefined, { sensitivity: 'base' })
  || (a.email || '').localeCompare(b.email || '', undefined, { sensitivity: 'base' });

const AdminPanel = () => {
  const { openProcessingReport } = useUploadPolling();
  const [currentTab, setCurrentTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  // Independent state for Join Link orgs (multi) and email
  const [joinOrgIds, setJoinOrgIds] = useState([]);
  const [joinEmail, setJoinEmail] = useState('');
  const [joinResult, setJoinResult] = useState(null);
  // Independent state for User Management orgs (multi)
  const [userOrgIds, setUserOrgIds] = useState([]);
  const [userOrgsLoading, setUserOrgsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showOrgErrorDialog, setShowOrgErrorDialog] = useState(false);
  const [orgErrorMessage, setOrgErrorMessage] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [profileFormData, setProfileFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    new_password: '',
    confirm_password: ''
  });
  const [profileErrors, setProfileErrors] = useState({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [waitingForReview, setWaitingForReview] = useState([]);
  const [reviewHistory, setReviewHistory] = useState([]);
  const [loadingWaitingForReview, setLoadingWaitingForReview] = useState(false);
  const [loadingReviewHistory, setLoadingReviewHistory] = useState(false);
  const [expandedReviewId, setExpandedReviewId] = useState(null);
  const [bulkDetailsRow, setBulkDetailsRow] = useState(null);
  const [reviewRow, setReviewRow] = useState(null);
  const [reviewSample, setReviewSample] = useState(null);
  const [loadingReviewSample, setLoadingReviewSample] = useState(false);
  const [reviewSampleError, setReviewSampleError] = useState('');
  const [reviewDecisionLoading, setReviewDecisionLoading] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [importLogRow, setImportLogRow] = useState(null);
  const [importLogText, setImportLogText] = useState('');
  const [loadingImportLog, setLoadingImportLog] = useState(false);
  const [importLogError, setImportLogError] = useState('');

  // Sync currentTab with URL hash on mount and when hash changes
  useEffect(() => {
    const syncTabFromHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'users' || hash === 'orgs' || hash === 'review') {
        setCurrentTab(hash);
      } else {
        // Default to 'users' if no hash or invalid hash
        setCurrentTab('users');
        window.location.hash = 'users';
      }
    };

    // Sync on mount
    syncTabFromHash();

    // Listen for hash changes
    window.addEventListener('hashchange', syncTabFromHash);
    return () => window.removeEventListener('hashchange', syncTabFromHash);
  }, []);

  useEffect(() => {
    const load = async (retryCount = 0) => {
      const maxRetries = 3;
      const retryDelay = 1000; // 1 second

      try {
        setUsersLoading(true);

        // Try cached user list from cookie (10 min); skip if missing organizations field
        const cachedUsersJson = getCookie(ADMIN_USERS_LIST_COOKIE);
        let usersFromCache = null;
        if (cachedUsersJson) {
          try {
            const parsed = JSON.parse(cachedUsersJson);
            if (isUsersListCacheValid(parsed)) {
              usersFromCache = parsed;
              setUsers(usersFromCache);
              setUsersLoading(false); // show cached list immediately
            } else {
              deleteCookie(ADMIN_USERS_LIST_COOKIE);
            }
          } catch (parseErr) {
            deleteCookie(ADMIN_USERS_LIST_COOKIE);
          }
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

        try {
          const requests = [api.get('/api/v2/admin/organizations', { signal: controller.signal })];
          if (usersFromCache == null) {
            requests.unshift(api.get('/api/v2/users/', { signal: controller.signal }));
          }

          const results = await Promise.all(requests);
          clearTimeout(timeoutId);

          let usersRes = null;
          let orgsRes;
          if (usersFromCache == null) {
            [usersRes, orgsRes] = results;
          } else {
            [orgsRes] = results;
          }

          if (usersRes) {
            if (usersRes.status === 200) {
              const usersData = usersRes.data;
              setUsers(usersData);
              try {
                setCookieWithMinutes(ADMIN_USERS_LIST_COOKIE, JSON.stringify(usersData), getAdminUsersListCacheMinutes());
              } catch (cookieErr) {
                // ignore cookie write failure (e.g. size limit)
              }
            } else if (usersRes.status >= 500 && retryCount < maxRetries) {
              setTimeout(() => load(retryCount + 1), retryDelay * (retryCount + 1));
              return;
            } else if (usersRes.status !== 200) {
              setErrorMessage('Failed to load users. Please try refreshing the page.');
              setShowErrorDialog(true);
            }
          }

          if (orgsRes.status === 200) {
            setOrganizations(orgsRes.data);
          } else if (orgsRes.status !== 200 && orgsRes.status < 500) {
            setErrorMessage('Failed to load organizations. Please try refreshing the page.');
            setShowErrorDialog(true);
          }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError' || fetchError.code === 'ECONNABORTED' || (fetchError.message && fetchError.message.includes('timeout'))) {
            if (retryCount < maxRetries) {
              setTimeout(() => load(retryCount + 1), retryDelay * (retryCount + 1));
              return;
            } else {
              setErrorMessage('Request timed out. Please refresh the page.');
              setShowErrorDialog(true);
            }
          } else if (retryCount < maxRetries) {
            setTimeout(() => load(retryCount + 1), retryDelay * (retryCount + 1));
            return;
          } else {
            throw fetchError;
          }
        } finally {
          setUsersLoading(false);
        }
      } catch (e) {
        if (retryCount < maxRetries) {
          setTimeout(() => load(retryCount + 1), retryDelay * (retryCount + 1));
        } else {
          setUsersLoading(false);
          setErrorMessage('Failed to load admin data. Please refresh the page.');
          setShowErrorDialog(true);
        }
      }
    };
    load();
  }, []);

  const handleCreateJoinLink = async () => {
    if (!joinEmail) {
      setErrorMessage('Please enter an email address.');
      setShowErrorDialog(true);
      return;
    }
    // Validate that at least one organization is selected
    const hasNoOrganizations = !joinOrgIds || (Array.isArray(joinOrgIds) && joinOrgIds.length === 0);
    if (hasNoOrganizations) {
      setOrgErrorMessage('Select at least one organization');
      setShowOrgErrorDialog(true);
      return;
    }
    setLoading(true);
    setJoinResult(null);
    try {
      await api.post('/api/v2/admin/users', { 
        email: joinEmail, 
        first_name: (joinResult?.first_name||''), 
        last_name: (joinResult?.last_name||''), 
        role: (joinResult?.role||''), 
        organization_ids: joinOrgIds 
      });
      // Response is successful, no need to parse data
      // Store email for display before clearing
      const createdEmail = joinEmail;
      // Clear form
      setJoinEmail('');
      setJoinOrgIds([]);
      // Clear all form fields including first_name, last_name, and role
      setJoinResult({ displayEmail: createdEmail });
      // Reload users list
      const usersRes = await api.get('/api/v2/users/');
      if (usersRes.status === 200) {
        const usersData = usersRes.data;
        setUsers(usersData.filter(u => u.is_active));
      }
      toast.success('User added successfully');
      organizationsService.clearOrganizations();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to add user. Please check the details and try again.');
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserOrganization = async () => {
    if (!selectedUser) {
      setErrorMessage('Please select a user first.');
      setShowErrorDialog(true);
      return;
    }
    // Validate that at least one organization is selected
    // Check both for null/undefined and empty array
    const hasNoOrganizations = !userOrgIds || (Array.isArray(userOrgIds) && userOrgIds.length === 0);
    if (hasNoOrganizations) {
      setOrgErrorMessage('Select at least one organization');
      setShowOrgErrorDialog(true);
      return;
    }
    
    setLoading(true);
    try {
      const payload = userOrgIds.map((id) => String(id));
      await api.put(`/api/v2/users/${selectedUser.id}/organizations`, payload);
      toast.success('User organizations updated');
      deleteCookie(ADMIN_USERS_LIST_COOKIE);
      const usersRes = await api.get('/api/v2/users/');
      if (usersRes.status === 200) {
        setUsers(usersRes.data);
        try {
          setCookieWithMinutes(ADMIN_USERS_LIST_COOKIE, JSON.stringify(usersRes.data), getAdminUsersListCacheMinutes());
        } catch {
          // ignore cookie write failure
        }
      }
      try {
        organizationsService.clearOrganizations();
      } catch {
        // ignore cookie clear failure
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      setErrorMessage(
        typeof detail === 'string' ? detail : 'Failed to update user organizations. Please try again.'
      );
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!selectedUser) {
      setErrorMessage('Please select a user first.');
      setShowErrorDialog(true);
      return;
    }
    setPasswordResetLoading(true);
    try {
      await api.post(`/api/v2/admin/users/${selectedUser.id}/password-reset`);
      toast.success('Password reset successfully to default password');
    } catch (error) {
      setErrorMessage('Failed to reset password. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (profileErrors[name]) {
      setProfileErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateProfileForm = () => {
    const newErrors = {};

    if (!profileFormData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!profileFormData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!profileFormData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(profileFormData.email)) {
      newErrors.email = 'Invalid email address';
    }

    // Only validate password fields if user wants to change password
    if (profileFormData.new_password || profileFormData.confirm_password) {
      if (!profileFormData.new_password) {
        newErrors.new_password = 'New password is required';
      } else if (profileFormData.new_password.length < 4) {
        newErrors.new_password = 'Password must be at least 4 characters';
      }
      
      if (profileFormData.new_password !== profileFormData.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match';
      }
    }

    setProfileErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateBasicInfo = async () => {
    // Validate basic fields only
    if (!profileFormData.first_name.trim()) {
      setProfileErrors({ first_name: 'First name is required' });
      return;
    }
    if (!profileFormData.last_name.trim()) {
      setProfileErrors({ last_name: 'Last name is required' });
      return;
    }

    setProfileLoading(true);
    
    try {
      // Update only first_name, last_name, and role (not email)
      const profileData = {
        first_name: profileFormData.first_name,
        last_name: profileFormData.last_name,
        role: profileFormData.role || null
      };
      
      const profileRes = await api.put(`/api/v2/admin/users/${selectedUser.id}/profile`, profileData);
      const updatedUser = profileRes.data;
      
      toast.success('Profile updated successfully!');
      deleteCookie(ADMIN_USERS_LIST_COOKIE);
      
      // Update the user in the local state
      setUsers(users.map(u => u.id === selectedUser.id ? {
        ...u,
        first_name: updatedUser.first_name || profileFormData.first_name,
        last_name: updatedUser.last_name || profileFormData.last_name,
        role: updatedUser.role !== undefined ? (updatedUser.role || '') : profileFormData.role
      } : u));
      
      // Update selected user
      setSelectedUser(prev => ({
        ...prev,
        first_name: updatedUser.first_name || profileFormData.first_name,
        last_name: updatedUser.last_name || profileFormData.last_name,
        role: updatedUser.role !== undefined ? (updatedUser.role || '') : profileFormData.role
      }));
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : (typeof error === 'string' ? error : 'Failed to update profile');
      setErrorMessage(errorMessage);
      setShowErrorDialog(true);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!validateProfileForm()) {
      return;
    }

    setProfileLoading(true);
    
    try {
      // Update profile information
      const profileData = {
        first_name: profileFormData.first_name,
        last_name: profileFormData.last_name,
        email: profileFormData.email,
        role: profileFormData.role || null
      };
      
      await api.put(`/api/v2/admin/users/${selectedUser.id}/profile`, profileData);
      
      // Change password if provided
      if (profileFormData.new_password) {
        await api.put(`/api/v2/admin/users/${selectedUser.id}/password`, { new_password: profileFormData.new_password });
      }
      
      toast.success('User profile updated successfully!');
      deleteCookie(ADMIN_USERS_LIST_COOKIE);
      
      // Update the user in the local state
      setUsers(users.map(u => u.id === selectedUser.id ? {
        ...u,
        first_name: profileFormData.first_name,
        last_name: profileFormData.last_name,
        email: profileFormData.email,
        role: profileFormData.role
      } : u));
      
      // Update selected user
      setSelectedUser(prev => ({
        ...prev,
        first_name: profileFormData.first_name,
        last_name: profileFormData.last_name,
        email: profileFormData.email,
        role: profileFormData.role
      }));
      
      // Clear password fields after successful update
      setProfileFormData(prev => ({
        ...prev,
        new_password: '',
        confirm_password: ''
      }));
      
      // Close the profile edit form
      setShowProfileEdit(false);
      
    } catch (error) {
      setErrorMessage(error.message || 'Failed to update user profile. Please check the details and try again.');
      setShowErrorDialog(true);
    } finally {
      setProfileLoading(false);
    }
  };

  const openProfileEdit = () => {
    setProfileFormData({
      first_name: selectedUser.first_name || '',
      last_name: selectedUser.last_name || '',
      email: selectedUser.email || '',
      role: selectedUser.role || '',
      new_password: '',
      confirm_password: ''
    });
    setProfileErrors({});
    setShowProfileEdit(true);
  };

  const handleToggleAdmin = async (userId, currentAdminStatus) => {
    setLoading(true);
    try {
      const target = !currentAdminStatus;
      await api.put(`/api/v2/users/${userId}/admin?is_admin=${target}`);
      setUsers(users.map(u => u.id === userId ? { ...u, is_admin: target } : u));
      deleteCookie(ADMIN_USERS_LIST_COOKIE);
      toast.success(`Admin status ${target ? 'granted' : 'removed'}`);
    } catch (error) {
      setErrorMessage('Failed to update admin status. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserActive = async (userId, currentActiveStatus) => {
    // If deactivating, show confirmation dialog
    if (currentActiveStatus) {
      const user = users.find(u => u.id === userId);
      if (user) {
        setUserToDelete(user);
        setShowDeleteDialog(true);
      }
      return;
    }
    
    // If activating, proceed directly
    setLoading(true);
    try {
      await api.put(`/api/v2/users/${userId}/active?is_active=true`);
      setUsers(users.map(u => u.id === userId ? { ...u, is_active: true } : u));
      deleteCookie(ADMIN_USERS_LIST_COOKIE);
      toast.success('User activated');
    } catch (error) {
      setErrorMessage('Failed to activate user. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    setLoading(true);
    try {
      await api.put(`/api/v2/users/${userToDelete.id}/active?is_active=false`);
      setUsers(users.map(u => u.id === userToDelete.id ? { ...u, is_active: false } : u));
      deleteCookie(ADMIN_USERS_LIST_COOKIE);
      toast.success('User deactivated successfully');
      setShowDeleteDialog(false);
      setUserToDelete(null);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to delete user. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const setTab = (tab) => {
    if (typeof window !== 'undefined') {
      setCurrentTab(tab);
      window.location.hash = tab;
    }
  };

  const loadReviewLists = useCallback(async () => {
    setLoadingWaitingForReview(true);
    setLoadingReviewHistory(true);
    try {
      const [waitingRes, historyRes] = await Promise.all([
        api.get('/api/v2/admin/bulk_file_uploads', { params: { approval_status: 'waiting' } }),
        api.get('/api/v2/admin/bulk_file_uploads', {
          params: { approval_status: 'approved,rejected' },
        }),
      ]);
      setWaitingForReview(Array.isArray(waitingRes.data) ? waitingRes.data : []);
      setReviewHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
    } catch (error) {
      console.error('Failed to load review lists:', error);
      setWaitingForReview([]);
      setReviewHistory([]);
      setErrorMessage('Failed to load waiting for review uploads. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setLoadingWaitingForReview(false);
      setLoadingReviewHistory(false);
    }
  }, []);

  useEffect(() => {
    if (currentTab === 'review') {
      loadReviewLists();
    }
  }, [currentTab, loadReviewLists]);

  const openReviewModal = async (row) => {
    setReviewRow(row);
    setReviewSample(null);
    setReviewSampleError('');
    setLoadingReviewSample(true);
    try {
      const res = await api.get(`/api/v2/admin/bulk_file_uploads/${row.upload_id}/validated_sample`);
      setReviewSample(res.data);
    } catch (error) {
      setReviewSampleError(
        error.response?.data?.detail || error.message || 'Failed to load validated sample.'
      );
    } finally {
      setLoadingReviewSample(false);
    }
  };

  const closeReviewModal = () => {
    if (reviewDecisionLoading) return;
    setReviewRow(null);
    setReviewSample(null);
    setReviewSampleError('');
    setShowApproveConfirm(false);
  };

  const submitReviewDecision = async (approvalStatus) => {
    if (!reviewRow?.upload_id) return;
    setReviewDecisionLoading(true);
    try {
      await api.put(`/api/v2/admin/bulk_file_uploads/${reviewRow.upload_id}/approval`, {
        approval_status: approvalStatus,
      });
      if (approvalStatus === 'approved') {
        if (!reviewRow.execution_id) {
          throw new Error('Upload has no execution_id; cannot start import.');
        }
        await api.post('/api/v2/import', {
          execution_id: reviewRow.execution_id,
          dry_run: false,
        });
        toast.success('Upload approved; import started');

      } else {
        toast.success('Upload rejected');
      }
      setShowApproveConfirm(false);
      setReviewRow(null);
      setReviewSample(null);
      setReviewSampleError('');
      await loadReviewLists();
    } catch (error) {
      setErrorMessage(
        error.response?.data?.detail || error.message || `Failed to ${approvalStatus} upload.`
      );
      setShowErrorDialog(true);
    } finally {
      setReviewDecisionLoading(false);
    }
  };

  const openImportLogModal = async (row) => {
    if (!row?.execution_id) {
      setErrorMessage('Upload has no execution_id; cannot load import logs.');
      setShowErrorDialog(true);
      return;
    }
    setImportLogRow(row);
    setImportLogText('');
    setImportLogError('');
    setLoadingImportLog(true);
    try {
      const res = await api.get('/api/v2/admin/import_logs', {
        params: { execution_id: row.execution_id },
      });
      setImportLogText(res.data?.log ?? '');
    } catch (error) {
      setImportLogError(
        error.response?.data?.detail || error.message || 'Failed to load import logs.'
      );
    } finally {
      setLoadingImportLog(false);
    }
  };

  const closeImportLogModal = () => {
    setImportLogRow(null);
    setImportLogText('');
    setImportLogError('');
    setLoadingImportLog(false);
  };

  const renderBulkReviewTable = (rows, { showApprovalStatus = false, showReport = false, showReview = false, emptyMessage }) => {
    if (!rows || rows.length === 0) {
      return <p className="text-sm text-gray-500">{emptyMessage}</p>;
    }
    // expand + time + user + file + [org if waiting] + [reviewer] + [approval] + [status] + actions
    const historyColSpan = showApprovalStatus ? 8 : 6;
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border border-gray-200 rounded-md">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-2 text-left font-medium text-gray-700 border-b w-9" aria-label="Expand" />
              <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Upload time</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">User</th>
              <th className="pl-1 pr-3 py-2 text-left font-medium text-gray-700 border-b">File name</th>
              {!showApprovalStatus && (
                <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Organization</th>
              )}
              {showApprovalStatus && (
                <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Reviewer</th>
              )}
              {showApprovalStatus && (
                <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Approval</th>
              )}
              {showApprovalStatus && (
                <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Status</th>
              )}
              <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[18rem]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const rowKey = `bulk-${row.id}`;
              const showProcessingReportButton =
                showReport &&
                (row.processing_status === 'validated' ||
                  row.processing_status === 'complete' ||
                  row.processing_status === 'error') &&
                Boolean(row.execution_id);
              const showImportLogsButton =
                showApprovalStatus &&
                row.import_status === 'complete' &&
                Boolean(row.execution_id);
              return (
                <React.Fragment key={rowKey}>
                  <tr
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedReviewId((prev) => (prev === rowKey ? null : rowKey))}
                  >
                    <td className="px-2 py-2 text-gray-500" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => setExpandedReviewId((prev) => (prev === rowKey ? null : rowKey))}
                        className="p-0.5 rounded hover:bg-gray-200"
                        aria-expanded={expandedReviewId === rowKey}
                      >
                        {expandedReviewId === rowKey ? (
                          <ChevronDown size={18} />
                        ) : (
                          <ChevronRight size={18} />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                      {row.upload_time ? new Date(row.upload_time).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap">{row.user_name || '—'}</td>
                    <td
                      className="pl-1 pr-3 py-2 text-gray-800 max-w-[12rem] whitespace-normal break-words"
                      title={row.file_name || undefined}
                    >
                      {row.file_name ? formatFileNameForDisplay(row.file_name) : '—'}
                    </td>
                    {!showApprovalStatus && (
                      <td className="px-3 py-2 text-gray-800">{row.organization_name || '—'}</td>
                    )}
                    {showApprovalStatus && (
                      <td className="px-3 py-2 text-gray-800">{row.reviewer_name || '—'}</td>
                    )}
                    {showApprovalStatus && (
                      <td className="px-3 py-2">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${approvalStatusBadgeClass(
                            row.approval_status
                          )}`}
                        >
                          {row.approval_status || '—'}
                        </span>
                      </td>
                    )}
                    {showApprovalStatus && (
                      <td className="px-3 py-2">
                        {row.import_status ? (
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${importStatusBadgeClass(
                              row.import_status
                            )}`}
                          >
                            {row.import_status}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2 flex-nowrap">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setBulkDetailsRow(row)}
                        >
                          Details
                        </Button>
                        {showProcessingReportButton && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => openProcessingReport(row.execution_id, { admin: true })}
                          >
                            Report
                          </Button>
                        )}
                        {showReview && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => openReviewModal(row)}
                          >
                            Review
                          </Button>
                        )}
                        {showImportLogsButton && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => openImportLogModal(row)}
                          >
                            Logs
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedReviewId === rowKey && (
                    <tr key={`${rowKey}-expanded`} className="border-b border-gray-100 bg-gray-50">
                      <td colSpan={historyColSpan} className="px-3 py-2">
                        <div className="text-xs text-gray-600 space-y-1">
                          <div>
                            <span className="font-medium text-gray-700">S3 URL:</span>{' '}
                            <code className="break-all bg-gray-100 px-2 py-1 rounded" title={row.s3_url}>
                              {row.s3_url || '—'}
                            </code>
                          </div>
                          {row.execution_id && (
                            <div>
                              <span className="font-medium text-gray-700">Execution ID:</span>{' '}
                              <code
                                className="break-all bg-gray-100 px-2 py-1 rounded"
                                title={row.execution_id}
                              >
                                {row.execution_id}
                              </code>
                            </div>
                          )}
                          {row.upload_id != null && (
                            <div>
                              <span className="font-medium text-gray-700">Upload ID:</span>{' '}
                              <code
                                className="break-all bg-gray-100 px-2 py-1 rounded"
                                title={String(row.upload_id)}
                              >
                                {row.upload_id}
                              </code>
                            </div>
                          )}
                          {row.file_md5 ? (
                            <div>
                              <span className="font-medium text-gray-700">File MD5:</span>{' '}
                              <code
                                className="break-all bg-gray-100 px-2 py-1 rounded"
                                title={row.file_md5}
                              >
                                {row.file_md5}
                              </code>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="pb-6 space-y-6">
      {/* Tabs */}
      <div className="flex space-x-2 border-b">
        <button
          className={`px-4 py-2 -mb-px border-b-2 ${currentTab === 'users' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600'}`}
          onClick={() => setTab('users')}
        >
          User Management
        </button>
        <button
          className={`px-4 py-2 -mb-px border-b-2 ${currentTab === 'orgs' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600'}`}
          onClick={() => setTab('orgs')}
        >
          Organization Management
        </button>
        <button
          className={`px-4 py-2 -mb-px border-b-2 ${currentTab === 'review' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-600'}`}
          onClick={() => setTab('review')}
        >
          Waiting for Review
        </button>
      </div>
      
      {currentTab === 'users' && (<>
      {/* Add New User */}
      <Card>
        <CardHeader>
          <CardTitle>Add New User</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Input
              id="joinEmail"
              value={joinEmail}
              onChange={e => setJoinEmail(e.target.value.replace(/[^a-zA-Z0-9@_.+-]/g, '').replace(/^@+/, '').replace(/^\d+/, ''))}
              placeholder="Invitee Email"
              aria-label="Invitee Email"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Input
                value={joinResult?.first_name || ''}
                onChange={e => setJoinResult(r => ({ ...(r||{}), first_name: e.target.value.replace(/[^a-zA-Z0-9-]/g, '').replace(/^\d+/, '') }))}
                placeholder="First Name"
                aria-label="First Name"
              />
            </div>
            <div className="space-y-2">
              <Input
                value={joinResult?.last_name || ''}
                onChange={e => setJoinResult(r => ({ ...(r||{}), last_name: e.target.value.replace(/[^a-zA-Z0-9-]/g, '').replace(/^\d+/, '') }))}
                placeholder="Last Name"
                aria-label="Last Name"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Input
              value={joinResult?.role || ''}
              onChange={e => setJoinResult(r => ({ ...(r||{}), role: e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').replace(/^\d+/, '') }))}
              placeholder="Role"
              aria-label="Role"
            />
          </div>
          <div className="space-y-2">
            <Label>Organizations (multi-select)</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[...organizations].sort((a, b) => a.name.localeCompare(b.name)).map(org => (
                <label key={org.id} className="flex items-center space-x-2 p-2 border rounded">
                  <input
                    type="checkbox"
                    checked={joinOrgIds.includes(org.id)}
                    onChange={(e) => {
                      setJoinOrgIds(prev => e.target.checked ? [...prev, org.id] : prev.filter(id => id !== org.id));
                    }}
                  />
                  <span>{org.name}</span>
                </label>
              ))}
            </div>
          </div>
          <Button variant="outline" onClick={handleCreateJoinLink} disabled={loading}>
            {loading ? 'Adding...' : 'Add'}
          </Button>
        </CardContent>
      </Card>

      {/* User Management */}
      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <FunctionalSelect
              label="User"
              value={selectedUser?.id?.toString()}
              onValueChange={(value) => {
                const user = users.find(u => u.id.toString() === value);
                setSelectedUser(user);
                // Load user's organizations
                (async () => {
                  setUserOrgsLoading(true);
                  try {
                    const res = await api.get(`/api/v2/users/${user.id}/organizations`);
                    const orgs = res.data;
                    setUserOrgIds(orgs.map(o => o.id));
                  } catch {
                    setUserOrgIds([]);
                  } finally {
                    setUserOrgsLoading(false);
                  }
                })();
              }}
              placeholder="Select user"
            >
              {[...users].sort(compareUsersByFirstName).map((user) => (
                <SelectItem key={user.id} value={user.id.toString()} disabled={!user.is_active}>
                  {user.first_name} {user.last_name} ({user.email}) {!user.is_active && '(Inactive)'}
                </SelectItem>
              ))}
            </FunctionalSelect>
          </div>
          
          {selectedUser && (
            <div className="space-y-2">
              {!selectedUser.is_active && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ This user is currently inactive. Their API keys have been disabled.
                  </p>
                </div>
              )}
              <Label>Organizations for User (multi-select)</Label>
              {userOrgsLoading ? (
                <div className="text-sm text-gray-500">Loading user organizations...</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[...organizations].sort((a, b) => a.name.localeCompare(b.name)).map(org => (
                    <label key={org.id} className="flex items-center space-x-2 p-2 border rounded">
                      <input
                        type="checkbox"
                        checked={userOrgIds.includes(org.id)}
                        onChange={(e) => {
                          setUserOrgIds(prev => e.target.checked ? [...prev, org.id] : prev.filter(id => id !== org.id));
                        }}
                        disabled={!selectedUser.is_active}
                      />
                      <span>{org.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleUpdateUserOrganization} disabled={loading || !selectedUser || !selectedUser.is_active}>
              {loading ? 'Updating...' : 'Update'}
            </Button>
            <Button variant="outline" onClick={handlePasswordReset} disabled={passwordResetLoading || !selectedUser || !selectedUser.is_active}>
              {passwordResetLoading ? 'Resetting...' : 'Password Reset'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={showOrgErrorDialog}
        onClose={() => setShowOrgErrorDialog(false)}
        className="w-full max-w-md mx-4"
      >
            <CardHeader className="pr-10">
              <CardTitle>Cannot Update Organizations</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 whitespace-pre-line">{orgErrorMessage}</p>
            </CardContent>
      </Modal>

      {/* Profile Editing Section */}
      {selectedUser && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="h-5 w-5 mr-2" />
              Profile Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!showProfileEdit ? (
              <div className="space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Current Profile</p>
                      <p className="text-sm text-gray-600">
                        <strong>Name:</strong> {selectedUser.first_name} {selectedUser.last_name}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Email:</strong> {selectedUser.email}
                      </p>
                    </div>
                    <div className="flex items-end">
                      <Button onClick={openProfileEdit} variant="outline">
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit Profile
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Editing Profile:</strong> {selectedUser.first_name} {selectedUser.last_name}
                  </p>
                </div>
                
                <form onSubmit={(e) => { e.preventDefault(); handleUpdateProfile(); }} className="space-y-4">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">
                        <User className="h-4 w-4 inline mr-2" />
                        First Name
                      </Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        value={profileFormData.first_name}
                        onChange={handleProfileInputChange}
                        placeholder="Enter first name"
                        className={profileErrors.first_name ? 'border-red-500' : ''}
                      />
                      {profileErrors.first_name && (
                        <p className="text-sm text-red-600">{profileErrors.first_name}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="last_name">
                        <User className="h-4 w-4 inline mr-2" />
                        Last Name
                      </Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        value={profileFormData.last_name}
                        onChange={handleProfileInputChange}
                        placeholder="Enter last name"
                        className={profileErrors.last_name ? 'border-red-500' : ''}
                      />
                      {profileErrors.last_name && (
                        <p className="text-sm text-red-600">{profileErrors.last_name}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role">
                      <User className="h-4 w-4 inline mr-2" />
                      Role
                    </Label>
                    <Input
                      id="role"
                      name="role"
                      value={profileFormData.role}
                      onChange={handleProfileInputChange}
                      placeholder="Enter role"
                      className={profileErrors.role ? 'border-red-500' : ''}
                    />
                    {profileErrors.role && (
                      <p className="text-sm text-red-600">{profileErrors.role}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">
                      <Mail className="h-4 w-4 inline mr-2" />
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={profileFormData.email}
                      onChange={handleProfileInputChange}
                      placeholder="Enter email"
                      className={profileErrors.email ? 'border-red-500' : ''}
                    />
                    {profileErrors.email && (
                      <p className="text-sm text-red-600">{profileErrors.email}</p>
                    )}
                  </div>

                  {/* Update Button for Basic Info */}
                  <div className="flex justify-start">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleUpdateBasicInfo}
                      disabled={profileLoading}
                      className="min-w-[120px]"
                    >
                      {profileLoading ? 'Updating...' : 'Update'}
                    </Button>
                  </div>

                  {/* Password Change Section */}
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Change Password</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Leave password fields blank if you don't want to change the password.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new_password">
                          <Lock className="h-4 w-4 inline mr-2" />
                          New Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="new_password"
                            name="new_password"
                            type={showNewPassword ? 'text' : 'password'}
                            value={profileFormData.new_password}
                            onChange={handleProfileInputChange}
                            placeholder="Enter new password"
                            className={profileErrors.new_password ? 'border-red-500 pr-10' : 'pr-10'}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            {showNewPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                        {profileErrors.new_password && (
                          <p className="text-sm text-red-600">{profileErrors.new_password}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirm_password">
                          <Lock className="h-4 w-4 inline mr-2" />
                          Confirm New Password
                        </Label>
                        <div className="relative">
                          <Input
                            id="confirm_password"
                            name="confirm_password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={profileFormData.confirm_password}
                            onChange={handleProfileInputChange}
                            placeholder="Confirm new password"
                            className={profileErrors.confirm_password ? 'border-red-500 pr-10' : 'pr-10'}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                              <Eye className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                        {profileErrors.confirm_password && (
                          <p className="text-sm text-red-600">{profileErrors.confirm_password}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-start space-x-3">
                    <Button
                      type="submit"
                      variant="outline"
                      disabled={profileLoading}
                      className="min-w-[120px]"
                    >
                      {profileLoading ? 'Updating...' : 'Update'}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
        </CardHeader>
        <CardContent>
          {!usersLoading && users.length > 0 && (
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by first name, last name or email..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          )}
          {usersLoading ? (
            <div className="text-center py-8 text-gray-500">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No users found</div>
          ) : (() => {
              const filteredUsers = [...users].filter((user) => {
                if (!userSearchQuery.trim()) return true;
                const q = userSearchQuery.trim().toLowerCase();
                const first = (user.first_name || '').toLowerCase();
                const last = (user.last_name || '').toLowerCase();
                const email = (user.email || '').toLowerCase();
                if (q.includes(' ')) {
                  const [qFirst, qLast] = q.split(/\s+/, 2).map((s) => s.trim()).filter(Boolean);
                  const matchFirst = !qFirst || first.startsWith(qFirst);
                  const matchLast = !qLast || last.startsWith(qLast);
                  return matchFirst && matchLast;
                }
                return first.includes(q) || last.includes(q) || email.includes(q);
              }).sort(compareUsersByFirstName);
              if (filteredUsers.length === 0) {
                return (
                  <div className="text-center py-8 text-gray-500">
                    {userSearchQuery.trim() ? 'No users match your search' : 'No users found'}
                  </div>
                );
              }
              return (
          <div className="space-y-4">
              {filteredUsers.map((user) => (
              <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">{user.first_name} {user.last_name}</p>
                  <p className="text-sm text-gray-600">{user.email}{user.role ? ` · ${user.role}` : ''}</p>
                  {(user.organizations?.length ?? 0) > 0 && (
                    <p className="text-sm text-gray-600 mt-1.5">{user.organizations.join(', ')}</p>
                  )}
                  <div className={`flex items-center space-x-2 text-sm text-gray-500${(user.organizations?.length ?? 0) > 0 ? ' mt-2' : ''}`}>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      user.is_admin ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.is_admin ? 'Admin' : 'User'}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant={user.is_active ? 'destructive' : 'outline'}
                    onClick={() => handleToggleUserActive(user.id, user.is_active)}
                    disabled={loading}
                    className="min-w-[120px]"
                    style={user.is_active ? { 
                      backgroundColor: '#F53D7A', 
                      borderColor: '#F53D7A',
                      color: '#FFFFFF'
                    } : undefined}
                    onMouseEnter={user.is_active && !loading ? (e) => {
                      e.target.style.backgroundColor = '#E02D6A';
                    } : undefined}
                    onMouseLeave={user.is_active && !loading ? (e) => {
                      e.target.style.backgroundColor = '#F53D7A';
                    } : undefined}
                  >
                    {user.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button
                    variant={user.is_admin ? 'destructive' : 'outline'}
                    onClick={() => handleToggleAdmin(user.id, user.is_admin)}
                    disabled={loading}
                    className="w-[150px]"
                    style={user.is_admin ? { 
                      backgroundColor: '#F53D7A', 
                      borderColor: '#F53D7A',
                      color: '#FFFFFF'
                    } : {}}
                    onMouseEnter={user.is_admin && !loading ? (e) => {
                      e.target.style.backgroundColor = '#E02D6A';
                    } : undefined}
                    onMouseLeave={user.is_admin && !loading ? (e) => {
                      e.target.style.backgroundColor = '#F53D7A';
                    } : undefined}
                  >
                    {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
              );
            })()}
        </CardContent>
      </Card>
      </>)}

      {currentTab === 'orgs' && (
        <Card>
          <CardHeader>
            <CardTitle>Organization Management</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <OrgManager organizations={organizations} setOrganizations={setOrganizations} />
          </CardContent>
        </Card>
      )}

      {currentTab === 'review' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Waiting for Review</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingWaitingForReview ? (
                <p className="text-sm text-gray-500">Loading uploads waiting for review...</p>
              ) : (
                renderBulkReviewTable(waitingForReview, {
                  showApprovalStatus: true,
                  showReport: true,
                  showReview: true,
                  emptyMessage: 'No bulk uploads waiting for review.',
                })
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Review History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingReviewHistory ? (
                <p className="text-sm text-gray-500">Loading review history...</p>
              ) : (
                renderBulkReviewTable(reviewHistory, {
                  showApprovalStatus: true,
                  showReport: true,
                  showReview: true,
                  emptyMessage: 'No reviewed bulk uploads yet.',
                })
              )}
            </CardContent>
          </Card>
        </>
      )}

      {bulkDetailsRow && (
        <Modal
          isOpen
          onClose={() => setBulkDetailsRow(null)}
          className="w-full max-w-lg max-h-[90vh] overflow-y-auto"
        >
          <CardHeader className="pr-10">
            <CardTitle>Bulk upload details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-800 space-y-5">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">File information</h4>
                <div className="space-y-1.5">
                  {BULK_METADATA_FIELDS.map((key) => (
                    <div key={key}>
                      <span className="font-medium text-gray-800">{BULK_METADATA_FIELD_LABELS[key]}:</span>{' '}
                      <span>{formatBulkDetailValue(bulkDetailsRow[key])}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Column mapping</h4>
                <div className="space-y-1.5">
                  {BULK_COLUMN_DETAIL_KEYS.map((key) => (
                    <div key={key}>
                      <span className="font-medium text-gray-800">{BULK_EXPANDED_FIELD_LABELS[key]}:</span>{' '}
                      <span>{formatBulkDetailValue(bulkDetailsRow[key])}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Modal>
      )}

      {importLogRow && (
        <Modal
          isOpen
          onClose={closeImportLogModal}
          className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        >
          <CardHeader className="pr-10">
            <CardTitle>Import logs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{importLogRow.file_name || '—'}</span>
              {importLogRow.execution_id ? (
                <span className="text-gray-500"> · {importLogRow.execution_id}</span>
              ) : null}
            </div>
            {loadingImportLog ? (
              <p className="text-sm text-gray-500">Loading import logs...</p>
            ) : importLogError ? (
              <p className="text-sm text-red-600">{importLogError}</p>
            ) : !importLogText ? (
              <p className="text-sm text-gray-500">No log content for this import.</p>
            ) : (
              <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-[60vh] whitespace-pre-wrap break-words">
                {importLogText}
              </pre>
            )}
          </CardContent>
        </Modal>
      )}

      {reviewRow && (
        <Modal
          isOpen
          onClose={closeReviewModal}
          className="w-full max-w-5xl max-h-[90vh] overflow-y-auto"
        >
          <CardHeader className="pr-10">
            <CardTitle>Review bulk upload</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{reviewRow.file_name || '—'}</span>
              {reviewRow.organization_name ? (
                <span className="text-gray-500"> · {reviewRow.organization_name}</span>
              ) : null}
            </div>
            {loadingReviewSample ? (
              <p className="text-sm text-gray-500">Loading sample rows...</p>
            ) : reviewSampleError ? (
              <p className="text-sm text-red-600">{reviewSampleError}</p>
            ) : !reviewSample || !reviewSample.rows || reviewSample.rows.length === 0 ? (
              <p className="text-sm text-gray-500">No sample rows found in the validated table.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200 rounded-md">
                  <thead className="bg-gray-50">
                    <tr>
                      {(reviewSample.columns || []).map((col) => (
                        <th
                          key={col}
                          className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reviewSample.rows.map((sampleRow, idx) => (
                      <tr key={idx} className="border-b border-gray-100">
                        {(reviewSample.columns || []).map((col) => (
                          <td key={col} className="px-3 py-2 text-gray-800 whitespace-nowrap">
                            {sampleRow[col] == null || sampleRow[col] === ''
                              ? '—'
                              : String(sampleRow[col])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={reviewDecisionLoading}
                onClick={() => setShowApproveConfirm(true)}
              >
                Approve
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={reviewDecisionLoading}
                onClick={() => submitReviewDecision('rejected')}
                style={{
                  backgroundColor: '#F53D7A',
                  borderColor: '#F53D7A',
                  color: '#FFFFFF',
                }}
                onMouseEnter={(e) => {
                  if (!reviewDecisionLoading) e.currentTarget.style.backgroundColor = '#E02D6A';
                }}
                onMouseLeave={(e) => {
                  if (!reviewDecisionLoading) e.currentTarget.style.backgroundColor = '#F53D7A';
                }}
              >
                {reviewDecisionLoading ? 'Saving...' : 'Reject'}
              </Button>
            </div>
          </CardContent>
        </Modal>
      )}

      {showApproveConfirm && reviewRow && (
        <Modal
          isOpen
          onClose={() => {
            if (reviewDecisionLoading) return;
            setShowApproveConfirm(false);
          }}
          onEnter={() => {
            if (!reviewDecisionLoading) submitReviewDecision('approved');
          }}
          className="w-full max-w-md mx-4"
        >
          <CardHeader className="pr-10">
            <CardTitle>Confirm</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              Are you sure you want to approve{' '}
              <span className="font-medium">{reviewRow.file_name || 'this file'}</span> and import it
              into FastCube delta tables?
            </p>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => submitReviewDecision('approved')}
                disabled={reviewDecisionLoading}
              >
                {reviewDecisionLoading ? 'Approving...' : 'OK'}
              </Button>
            </div>
          </CardContent>
        </Modal>
      )}

      {showDeleteDialog && userToDelete && (
      <Modal
        isOpen
        onClose={() => {
          setShowDeleteDialog(false);
          setUserToDelete(null);
        }}
        onEnter={() => {
          if (!loading) handleConfirmDelete();
        }}
        className="w-full max-w-md mx-4"
      >
            <CardHeader className="pr-10">
              <CardTitle>Confirm Delete</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete {userToDelete.email}?
              </p>
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleConfirmDelete}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'OK'}
                </Button>
              </div>
            </CardContent>
      </Modal>
      )}

      {/* Error Dialog */}
      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        message={errorMessage}
      />
    </div>
  );
};

function OrgManager({ organizations, setOrganizations }) {
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showExistsDialog, setShowExistsDialog] = useState(false);
  const [existsOrgName, setExistsOrgName] = useState('');
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgUsersName, setOrgUsersName] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showPublishersDialog, setShowPublishersDialog] = useState(false);
  const [orgPublishersName, setOrgPublishersName] = useState('');
  const [orgPublishersOrgId, setOrgPublishersOrgId] = useState(null);
  const [orgPublisherIds, setOrgPublisherIds] = useState([]);
  const [newPublisherId, setNewPublisherId] = useState('');
  const [updatingPublishers, setUpdatingPublishers] = useState(false);
  const [createOrgLoading, setCreateOrgLoading] = useState(false);
  const [renameOrgLoading, setRenameOrgLoading] = useState(false);
  const [showPropertiesDialog, setShowPropertiesDialog] = useState(false);
  const [propertiesOrg, setPropertiesOrg] = useState(null);

  const createOrg = async () => {
    if (!name.trim()) return;
    setCreateOrgLoading(true);
    try {
      const orgRes = await api.post('/api/v2/organizations/', { name });
      const org = orgRes.data;
      // Reload organizations list to include reactivated organizations
      try {
        const orgsRes = await api.get('/api/v2/admin/organizations');
        if (orgsRes.status === 200) {
          const allOrgs = orgsRes.data;
          setOrganizations(allOrgs);
        } else {
          // Fallback: add the new org to the list
          setOrganizations([...organizations, org]);
        }
      } catch {
        // Fallback: add the new org to the list
        setOrganizations([...organizations, org]);
      }
      setName('');
      toast.success('Organization created successfully');
      organizationsService.clearOrganizations();
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      const detailStr = typeof data?.detail === 'string'
        ? data.detail
        : Array.isArray(data?.detail)
          ? data.detail.map(d => d?.msg ?? d).join(' ')
          : data?.detail != null ? String(data.detail) : '';
      const apiMessage = detailStr.trim() || null;

      if (status === 400 && apiMessage && apiMessage.includes('already exists')) {
        setExistsOrgName(name);
        setShowExistsDialog(true);
        return;
      }

      if (apiMessage) {
        setErrorMessage(apiMessage);
      } else if (status === 502) {
        setErrorMessage(
          'Server returned 502 Bad Gateway. Organization was not created. ' +
          'This usually means S3 bucket creation failed (permissions, region, or bucket name). Check backend logs for the actual error.'
        );
      } else {
        setErrorMessage('Failed to create organization. Please check the details and try again.');
      }
      setShowErrorDialog(true);
    } finally {
      setCreateOrgLoading(false);
    }
  };

  const handleEditClick = (id, currentName) => {
    setEditingId(id);
    setEditingName(currentName);
  };

  const updateOrg = async (id) => {
    if (!editingName.trim() || renameOrgLoading) return;
    setRenameOrgLoading(true);
    try {
      const res = await api.put(`/api/v2/organizations/${id}`, { name: editingName });
      const updated = res.data;
      setOrganizations(organizations.map(o => o.id === id ? updated : o));
      setEditingId(null);
      setEditingName('');
      organizationsService.clearOrganizations();
    } catch (error) {
      try {
        const err = error.response?.data || {};
        // eslint-disable-next-line no-console
        console.warn('Org update failed', err);
        // Check if this is a member error (400 status) - show dialog instead of toast
        if (error.response?.status === 400 && err?.detail) {
          setErrorMessage(err.detail);
          setShowErrorDialog(true);
        } else {
          setErrorMessage(err?.detail || 'Failed to update organization. Please try again.');
          setShowErrorDialog(true);
        }
      } catch {
        setErrorMessage('Failed to update organization. Please try again.');
        setShowErrorDialog(true);
      }
    } finally {
      setRenameOrgLoading(false);
    }
  };

  const handleUsersClick = async (org) => {
    setLoadingUsers(true);
    setOrgUsersName(org.name);
    try {
      const res = await api.get(`/api/v2/organizations/${org.id}/users`);
      const data = res.data;
      setOrgUsers(data.users || []);
      setShowUsersDialog(true);
    } catch (error) {
      setErrorMessage('Failed to load users. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handlePublishersClick = (org) => {
    const publisherIds = (org.publisher_ids || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    setOrgPublishersName(org.name);
    setOrgPublishersOrgId(org.id);
    setOrgPublisherIds(publisherIds);
    setNewPublisherId('');
    setShowPublishersDialog(true);
  };

  const closePublishersDialog = () => {
    setShowPublishersDialog(false);
    setOrgPublisherIds([]);
    setOrgPublishersName('');
    setOrgPublishersOrgId(null);
    setNewPublisherId('');
  };

  const savePublisherIds = async (updatedPublisherIds, successMessage) => {
    const publisherIdsCsv = updatedPublisherIds.join(',');

    setUpdatingPublishers(true);
    try {
      const res = await api.put(`/api/v2/organizations/${orgPublishersOrgId}`, {
        publisher_ids: publisherIdsCsv,
      });
      const updated = res.data;
      setOrganizations(organizations.map((o) => (o.id === orgPublishersOrgId ? updated : o)));
      setOrgPublisherIds(updatedPublisherIds);
      toast.success(successMessage);
      organizationsService.clearOrganizations();
    } catch (error) {
      const err = error.response?.data || {};
      setErrorMessage(err?.detail || 'Failed to update publisher IDs. Please try again.');
      setShowErrorDialog(true);
    } finally {
      setUpdatingPublishers(false);
    }
  };

  const handleAddPublisherId = async () => {
    const trimmedId = newPublisherId.trim();
    if (!trimmedId || !orgPublishersOrgId) return;

    if (orgPublisherIds.includes(trimmedId)) {
      setErrorMessage('Publisher ID already exists for this organization.');
      setShowErrorDialog(true);
      return;
    }

    const updatedPublisherIds = [...orgPublisherIds, trimmedId];
    await savePublisherIds(updatedPublisherIds, 'Publisher ID added successfully');
    setNewPublisherId('');
  };

  const handleRemovePublisherId = async (publisherIdToRemove) => {
    if (!orgPublishersOrgId) return;

    const updatedPublisherIds = orgPublisherIds.filter((id) => id !== publisherIdToRemove);
    await savePublisherIds(updatedPublisherIds, 'Publisher ID removed successfully');
  };

  const handleDeleteClick = (org) => {
    setOrgToDelete(org);
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteOrg = async () => {
    if (!orgToDelete) return;
    
    try {
      await api.delete(`/api/v2/organizations/${orgToDelete.id}`);
      setOrganizations(organizations.filter(o => o.id !== orgToDelete.id));
      setShowDeleteConfirmDialog(false);
      setOrgToDelete(null);
      toast.success('Organization deleted successfully');
      organizationsService.clearOrganizations();
    } catch (error) {
      try {
        const err = error.response?.data || {};
        // eslint-disable-next-line no-console
        console.warn('Org delete failed', err);
        // Check if this is a member error (400 status) - show dialog instead of toast
        if (error.response?.status === 400 && err?.detail) {
          setErrorMessage(err.detail);
          setShowErrorDialog(true);
          setShowDeleteConfirmDialog(false);
        } else {
          setErrorMessage(err?.detail || 'Failed to delete organization. Please try again.');
          setShowErrorDialog(true);
        }
      } catch {
        setErrorMessage('Failed to delete organization. Please try again.');
        setShowErrorDialog(true);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex space-x-2">
        <Input
          value={name}
          onChange={e => setName(e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').replace(/^\d+/, ''))}
          placeholder="New organization name (letters, numbers, hyphens, underscores; cannot start with a number)"
          disabled={createOrgLoading}
        />
        <Button variant="outline" onClick={createOrg} disabled={createOrgLoading}>
          {createOrgLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />
              Creating...
            </>
          ) : (
            'Create'
          )}
        </Button>
      </div>

      <div className="space-y-2">
        {[...organizations].sort((a, b) => a.name.localeCompare(b.name)).map(org => (
          <div key={org.id} className="flex items-center justify-between p-2 border rounded">
            {editingId === org.id ? (
              <>
                <Input
                className="flex-1 mr-2"
                value={editingName}
                onChange={e => setEditingName(e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').replace(/^\d+/, ''))}
                disabled={renameOrgLoading}
              />
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateOrg(org.id)}
                    disabled={renameOrgLoading}
                  >
                    {renameOrgLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setEditingId(null); setEditingName(''); }}
                    disabled={renameOrgLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                <span className="flex-1">{org.name}</span>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleUsersClick(org)}
                    disabled={loadingUsers}
                  >
                    Users
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePublishersClick(org)}
                  >
                    Publishers
                  </Button>
                  {org.name !== FASTCUBE_ORG ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setPropertiesOrg(org);
                        setShowPropertiesDialog(true);
                      }}
                    >
                      Properties
                    </Button>
                  ) : null}
                  <Button size="sm" variant="outline" onClick={() => handleEditClick(org.id, org.name)}>Rename</Button>
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    onClick={() => handleDeleteClick(org)}
                    style={{ 
                      backgroundColor: '#F53D7A', 
                      borderColor: '#F53D7A',
                      color: '#FFFFFF'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#E02D6A';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#F53D7A';
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <Modal
        isOpen={showExistsDialog}
        onClose={() => {
          setShowExistsDialog(false);
          setExistsOrgName('');
        }}
        className="w-full max-w-md mx-4"
      >
            <CardHeader className="pr-10">
              <CardTitle>Organization Already Exists</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                Organization {existsOrgName} already exists
              </p>
            </CardContent>
      </Modal>

      {showDeleteConfirmDialog && orgToDelete && (
      <Modal
        isOpen
        onClose={() => {
          setShowDeleteConfirmDialog(false);
          setOrgToDelete(null);
        }}
        onEnter={confirmDeleteOrg}
        className="w-full max-w-md mx-4"
      >
            <CardHeader className="pr-10">
              <CardTitle>Confirm Delete</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete organization {orgToDelete.name}?
              </p>
              <div className="flex justify-end">
                <Button variant="outline" onClick={confirmDeleteOrg}>
                  Delete
                </Button>
              </div>
            </CardContent>
      </Modal>
      )}

      <Modal
        isOpen={showUsersDialog}
        onClose={() => {
          setShowUsersDialog(false);
          setOrgUsers([]);
          setOrgUsersName('');
        }}
        className="w-full max-w-md mx-4"
      >
            <CardHeader className="pr-10">
              <CardTitle>{orgUsersName} users:</CardTitle>
            </CardHeader>
            <CardContent>
              {orgUsers.length > 0 ? (
                <div className="space-y-2">
                  {orgUsers.map((email, index) => (
                    <p key={index} className="text-gray-700">{email}</p>
                  ))}
                </div>
              ) : (
                <p className="text-gray-700">No users found for this organization.</p>
              )}
            </CardContent>
      </Modal>

      <Modal
        isOpen={showPropertiesDialog}
        onClose={() => {
          setShowPropertiesDialog(false);
          setPropertiesOrg(null);
        }}
        className="w-full max-w-2xl mx-4"
      >
        <CardHeader className="pr-10">
          <CardTitle>{propertiesOrg?.name} properties</CardTitle>
        </CardHeader>
        <CardContent>
          {propertiesOrg ? (
            <OrganizationPropertiesEditor
              organizationId={propertiesOrg.id}
              apiBase="/api/v2/admin/organizations"
            />
          ) : null}
        </CardContent>
      </Modal>

      <Modal
        isOpen={showPublishersDialog}
        onClose={closePublishersDialog}
        className="w-full max-w-md mx-4"
      >
            <CardHeader className="pr-10">
              <CardTitle>{orgPublishersName} publishers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex space-x-2 mb-4">
                <Input
                  value={newPublisherId}
                  onChange={(e) => setNewPublisherId(e.target.value.replace(/\D/g, ''))}
                  placeholder="Publisher ID"
                  disabled={updatingPublishers}
                />
                <Button
                  variant="outline"
                  onClick={handleAddPublisherId}
                  disabled={updatingPublishers || !newPublisherId.trim()}
                >
                  {updatingPublishers ? 'Saving...' : 'Add'}
                </Button>
              </div>
              {orgPublisherIds.length > 0 ? (
                <div className="space-y-2">
                  {orgPublisherIds.map((publisherId) => (
                    <div
                      key={publisherId}
                      className="flex items-center justify-between rounded-md bg-blue-50 px-3 py-2"
                    >
                      <span className="text-gray-700">{publisherId}</span>
                      <button
                        type="button"
                        onClick={() => handleRemovePublisherId(publisherId)}
                        disabled={updatingPublishers}
                        className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        aria-label={`Remove publisher ID ${publisherId}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-700">No publisher IDs found for this organization.</p>
              )}
            </CardContent>
      </Modal>

      {/* Error Dialog */}
      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Error"
        message={errorMessage}
      />
    </div>
  );
}

export default AdminPanel;
